import classNames from 'classnames';
import {WithRouterProps} from 'react-router';
import React, {useEffect} from 'react';

import {LoadingSpinner} from '@albert-io/atomic';
import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import ClassroomInvitations from './ClassroomInvitations/ClassroomInvitations';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import {history} from 'client/history';
import JoinClassroomModal from 'client/Classrooms/JoinClassroom/JoinClassroomModal';
import SidebarDesktop from './Sidebar/SidebarDesktop';
import SidebarMobile from './Sidebar/SidebarMobile';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageTabs from './StudentHomepageTabs';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import useMaybeRedirectUser from './hooks/useMaybeRedirectUser';
import useOnClassroomEnrollment from './hooks/useOnClassroomEnrollment';
import usePrevious from 'lib/hooks/usePrevious';
import useStudent from './hooks/useStudent';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

export const TRANSITION_DELAY_CLASSNAME = 'delay-200';
export const TRANSITION_DURATION_CLASSNAME = 'duration-200';

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  const {isSAndDown} = useBreakpoint();
  const lastIsSAnddown = usePrevious(isSAndDown);

  const {classroomId} = params;

  const {fetchStudent, isStudentLoading} = useStudent();
  const onClassroomEnrollment = useOnClassroomEnrollment(classroomId);

  const countClassrooms = useStudentDashboard((state) => state.classrooms.size);
  const exceededHardCapLimit = useStudentDashboard((state) => state.exceededHardCapLimit);
  const hasInvitations = useStudentDashboard((state) => state.invitations.size > 0);
  const isSidebarExpanded = useStudentDashboard((state) => state.isSidebarExpanded);
  const setIsSidebarExpanded = useStudentDashboard((state) => state.setIsSidebarExpanded);
  const setShowJoinClassModal = useStudentDashboard((state) => state.setShowJoinClassModal);
  const showJoinClassModal = useStudentDashboard((state) => state.showJoinClassModal);
  const studentHasInvalidEmail = useStudentDashboard((state) => state.studentHasInvalidEmail);

  const isWelcomePage = location.pathname.includes('welcome');
  const showTabs = !isWelcomePage;

  const isMaybeRedirectingUser = useMaybeRedirectUser(classroomId, location.pathname);

  const showLoadingSpinner = isStudentLoading || isMaybeRedirectingUser;

  useEffect(() => {
    fetchStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callAction(appActions.SET_FOOTER_DISPLAY, false);

    return () => {
      callAction(appActions.SET_FOOTER_DISPLAY, true);
    };
  }, []);

  // Force sidebar to expand/contract when viewport changes to/from mobile
  useEffect(() => {
    if (isSAndDown !== lastIsSAnddown) {
      setIsSidebarExpanded(!isSAndDown);
    }
  }, [isSAndDown, lastIsSAnddown, setIsSidebarExpanded]);

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div
        className={classNames(
          TRANSITION_DURATION_CLASSNAME,
          '-mt-[60px] flex h-[calc(100vh-60px)] flex-col background-subtle m:grid m:grid-cols-[clamp(280px,20vw,320px)_auto] m:grid-rows-[repeat(3,min-content)_1fr]',
          {
            'justify-center': showLoadingSpinner,
            'm:grid-cols-[0_auto]!': !isSidebarExpanded,
            [TRANSITION_DELAY_CLASSNAME]: !isSidebarExpanded
          }
        )}
      >
        {showLoadingSpinner ? (
          <LoadingSpinner
            className='m:col-span-full m:row-span-4 m:self-center'
            bottomMarginLevel={0}
            size={5}
          />
        ) : (
          <>
            <StudentHomepageHeader className='m:col-span-full' classroomId={classroomId} />
            <SidebarDesktop className='m:row-span-3' pathname={location.pathname} />
            <ClassroomInvitations
              className='mx-x1 mt-x3 s:mx-x2'
              classroomId={classroomId}
              isWelcomePage={isWelcomePage}
            />
            {showTabs && (
              <StudentHomepageTabs
                className={classNames('mx-x1 shrink-0 s:mx-x2', {'mt-x3': !hasInvitations})}
                classroomId={classroomId}
                pathname={location.pathname}
              />
            )}
            <div
              className={classNames(
                'mx-x1 overflow-y-auto rounded-b-3xl background-primary s:mx-x2 m:max-h-full m:self-start',
                {
                  'm:mt-x4': !showTabs && hasInvitations,
                  'row-span-2': !showTabs || !hasInvitations,
                  'row-span-3 mt-x3': !showTabs && !hasInvitations,
                  'rounded-t-3xl': !showTabs
                }
              )}
              // Used to allow intersection observers to observe this as the root scrolling context
              id='tabContentContainer'
              // Unmounts and re-mounts the tab content when the classroom scope or number of classrooms change.
              // This significantly simplifies code since the dashboard tabs care a lot about the classroom scope.
              // See: https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
              key={`tab-content-${classroomId ?? `home-${countClassrooms}`}`}
            >
              {children}
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinClassroomModal
                handleClose={() => setShowJoinClassModal(false)}
                isNewStudentDashboard
                onEnrollSuccess={() => {
                  setShowJoinClassModal(false);
                  onClassroomEnrollment();
                  if (isWelcomePage) {
                    history.replace('/home/assignments');
                  }
                }}
              />
            )}
          </>
        )}
      </div>
      {!showLoadingSpinner && <SidebarMobile pathname={location.pathname} />}
    </FeatureFlag>
  );
}
